// Pure logic behind the balances screen (src/user/views/balances.jsx) and the header read-out
// (src/user/modules/layout_elements/balances.jsx + balances_inner.jsx).
//
// Two sources feed one view, exactly as React had it:
//   - the market-data websocket pushes the live `[{ asset, balance }]` list, and
//   - GET /ui/balances returns `{ balances: [{ asset, available, estimatedValue }], valueAsset,
//     totalAccountValue }`, whose estimates are what the "(≈ 123.45 EUR)" suffix shows.
// The socket is authoritative for the amount; the REST snapshot only supplies the estimate.

// The fiat asset that balances are estimated in. React kept it in this cookie (written by the
// settings "Estimated values asset" form, read back in layout.jsx to parameterise GET /ui/balances).
export const VALUE_ASSET_COOKIE = "estimated-value-asset";

// react-cookie stored the whole react-select option object ({ value, label }) URI-encoded, and
// layout.jsx read `cookies["estimated-value-asset"]?.value`. Accept that shape, a bare asset code
// (what a hand-set cookie or a future writer would use), and anything unparseable → null.
export const parseValueAssetCookie = (raw) => {
  if (!raw) return null;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    /* a malformed %-escape — fall back to the raw value */
  }
  if (!decoded.startsWith("{")) return decoded || null;
  try {
    return JSON.parse(decoded)?.value ?? null;
  } catch {
    return null;
  }
};

// Written back in React's shape so the cookie stays readable by the React build during the
// per-env cutover (and by a rollback to it).
export const serializeValueAssetCookie = (asset) =>
  encodeURIComponent(JSON.stringify({ value: asset, label: asset }));

// The currencies React's "Estimated values asset" picker offered
// (settings_forms_estimated_values.tsx). GET /ui/balances takes any asset the backend knows, and
// these are not necessarily in a tenant's configured fiat list — PLN and JPY typically are not — so
// deriving the picker purely from that list silently took options away from anyone using them.
export const VALUE_ASSET_CHOICES = ["EUR", "USD", "GBP", "PLN", "JPY"];

// Options for the estimation-asset picker: React's fixed five, plus any configured fiat asset it
// does not already cover (so a newly configured currency appears without a front-end release), plus
// whatever is currently selected (so a stored choice is never silently dropped from its own
// dropdown). Order is stable and duplicate-free.
export const valueAssetOptions = (configuredFiat = [], selected = "") => {
  const seen = new Set();
  return [...VALUE_ASSET_CHOICES, ...configuredFiat, selected].filter((code) => {
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
};

// Splits the live balances into the two columns the screen renders. `fiatCodes` comes from
// GET /ui/assets/config (the assets whose assetType is Fiat); everything else is a coin, which is
// the same "not in the fiat list" rule React used, so an asset missing from the config still shows
// up rather than disappearing.
export const splitBalances = (balances, fiatCodes) => {
  const fiat = new Set(fiatCodes ?? []);
  const entries = balances ?? [];
  return {
    fiat: entries.filter((entry) => fiat.has(entry.asset)),
    coin: entries.filter((entry) => !fiat.has(entry.asset)),
  };
};

// The estimated value of one asset, from the REST snapshot. Returns null when the snapshot has no
// row for that asset (a balance that arrived over the socket after the snapshot was taken) or when
// the asset IS the value asset — React suppressed the "(x EUR)" suffix on the EUR row itself,
// where it would just repeat the balance.
export const estimatedValueOf = (snapshot, asset) => {
  if (!snapshot || asset === snapshot.valueAsset) return null;
  const row = (snapshot.balances ?? []).find((entry) => entry.asset === asset);
  return row?.estimatedValue ?? null;
};
